import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import Tuner from '../tuner';
import { tuningsForInstrument } from '../tuner/tunings';
import { referenceSoundFor } from '../tuner/samples';
import BackingTrack, { useBackingTrack } from '../backing-track';
import { parseChord } from '../backing-track/chords';
import { SvgChordChange, SvgChordGrid, SvgDrum, SvgMetronome, SvgPracticeLog, SvgTuningFork } from '../svg';
import { openModal } from '../../api/modals/actions';
import { _saveProfile, updateProfile } from '../../api/profile/actions';
import { store } from '../../store';
import { track, trackOnce } from '../../usage';
import { connectWithStore } from '../../utils';
import { closeAudio, isAudioSupported } from './audio';
import ChangesTrainer from './changes-trainer';
import ChordsPanel, { upcomingChord } from './chords-panel';
import useDockPosition, { DockHandle } from './dock-position';
import Metronome from './metronome';
import PracticeLog from './practice-log';
import { DRONE_KEY, HELP_KEY, HINTS_KEY_LABEL, KeyHint, NEXT_KEY, STOP_KEY, ShortcutsHelp, TOOL_KEYS, isShortcut, useKeyHints } from './shortcuts';
import { loadStored, saveStored } from './storage';
import ChordPlayer, { chordNotes } from './strum';
import usePracticeLog from './use-practice-log';
import useTransport from './use-transport';

// The practice dock: a toolbar pinned to the bottom of the song page (or
// where a subscriber dragged it, see ./dock-position) for tools you play
// along with. The settings bar above the song is for how the song is shown.
//
// Each tool gets a tab, and its panel opens above the bar (below it when the
// dock is near the top) so the song stays readable. A panel is only mounted
// while it's open, so the tuner lets go of the microphone as soon as it's
// closed. What keeps going with its panel closed (the metronome, the backing
// track, following the player through the song, the drone) lives here in the
// dock, and the bar shows it's on and a stop button.
//
// The tools share one AudioContext and one microphone (see ./audio), and the
// metronome and backing track share one clock (see ./use-transport).
//
// Keyboard shortcuts (see ./shortcuts): a letter opens each tool, Space
// starts or stops what the open panel plays, S stops everything, + and -
// change the tempo, Enter taps it, N plays the next chord, D the drone,
// 1–9 the song's chords (chords panel), and ? lists them all.
//
// Chord names are the shapes the player plays, so with a capo on they sound
// higher than written. The tools that listen for chords or play them follow
// the capo the song page shows, which the player can say they aren't using.

// Profile key holding the tuner's settings (see Tuner for the shape).
const TUNER_SETTINGS_KEY = 'tuner'
// Quick changes (stepping the reference pitch, flicking through tunings) are
// sent as one request once the user pauses: two requests in flight can
// overwrite each other on the server.
const SAVE_AFTER = 500

// Whether the player has the capo on, per song: { [songId]: { fret, on } }.
const CAPO_KEY = 'gp-practice-capo'

// The song's own capo is taken to be on and the one the page suggests after
// transposing to be off, until the player says otherwise for that song and
// fret.
function capoOnFor(songId, fret, isHint) {
    const stored = (loadStored(CAPO_KEY) || {})[songId]
    return stored && stored.fret === fret ? !!stored.on : !isHint
}

// capo: the fret the song page tells the player to put a capo on (0 for
// none); capoIsHint: it's the page's suggestion for playing a transposed
// song in the original key rather than the song's own capo.
function PracticeDock({ songId, song, capo = 0, capoIsHint = false, instruments, instrumentPreference, tunerSettings, user, dispatch }) {
    const [openTool, setOpenTool] = useState(null)
    const [capoChoice, setCapoChoice] = useState(null)
    const [droneOn, setDroneOn] = useState(false)
    const [trainerRunning, setTrainerRunning] = useState(false)
    const dockRef = useRef(null)
    const saveTimer = useRef(null)
    const shortcutRef = useRef(null)
    const keyHints = useKeyHints()
    const playerRef = useRef(null)
    if (!playerRef.current) {
        playerRef.current = new ChordPlayer()
    }
    const player = playerRef.current
    const isSubscriber = !!user.hasSubscription
    // The backing track is only for superusers while it's being worked on.
    // Everyone else gets no tab for it, and only its panel starts the band.
    const canUseBand = !!(user.isAuthenticated && user.is_superuser)
    const dockPosition = useDockPosition(dockRef, user, () => requireSubscription('move-practice-dock'))

    const storedCapoOn = useMemo(() => capoOnFor(songId, capo, capoIsHint), [songId, capo, capoIsHint])
    const capoOn = capoChoice && capoChoice.songId === songId && capoChoice.fret === capo ? capoChoice.on : storedCapoOn
    const soundingCapo = capo > 0 && capoOn ? capo : 0
    const toggleCapo = () => {
        const on = !capoOn
        setCapoChoice({ songId, fret: capo, on })
        track('dock_capo_toggled', { on })
        saveStored(CAPO_KEY, { ...(loadStored(CAPO_KEY) || {}), [songId]: { fret: capo, on } })
    }
    useEffect(() => {
        player.setCapo(soundingCapo)
    }, [soundingCapo])

    const transport = useTransport(songId, { isSubscriber })
    const backingTrack = useBackingTrack(song, {
        transport,
        isSubscriber,
        capo: soundingCapo,
        onRequireSubscription: () => requireSubscription('backing-track'),
    })
    // Logging out takes the tab away, and with it the way to stop the band.
    useEffect(() => {
        if (!canUseBand) backingTrack.stop()
    }, [canUseBand])
    const practiceLog = usePracticeLog({
        songId,
        isAuthenticated: !!user.isAuthenticated,
        practising: transport.running || backingTrack.active || trainerRunning,
    })

    const instrument = instruments.find(i => i.id === instrumentPreference)
    const tuning = useMemo(() => {
        const tunings = instrument ? tuningsForInstrument(instrument, []) : []
        return tunings.length ? tunings[0].notes : null
    }, [instrument])
    const chordsByName = useMemo(() => {
        const byName = {}
        ;(song.chords || []).forEach(chord => {
            if (chord && chord.name && !byName[chord.name]) byName[chord.name] = chord
        })
        return byName
    }, [song.chords])
    const songKey = song.transposed_to || song.key || ''
    const sound = referenceSoundFor(instrument)
    useEffect(() => {
        player.setSound(sound)
    }, [sound])

    const saveTunerSettingsNow = () => {
        clearTimeout(saveTimer.current)
        saveTimer.current = null
        dispatch(updateProfile(TUNER_SETTINGS_KEY, store.getState().profile.ui_preferences[TUNER_SETTINGS_KEY]))
    }

    const changeTunerSettings = (settings) => {
        dispatch(_saveProfile(TUNER_SETTINGS_KEY, settings))
        if (!user.isAuthenticated) {
            return
        }
        clearTimeout(saveTimer.current)
        saveTimer.current = setTimeout(saveTunerSettingsNow, SAVE_AFTER)
    }

    // Leave room at the bottom of the page so the end of the song (and the
    // footer) can be scrolled clear of the dock and an open panel.
    useEffect(() => {
        const dock = dockRef.current
        const root = document.documentElement
        const update = () => root.style.setProperty('--practice-dock-space', `${dock.offsetHeight}px`)
        document.body.classList.add('has-practice-dock')
        update()
        const observer = window.ResizeObserver ? new window.ResizeObserver(update) : null
        if (observer) {
            observer.observe(dock)
        }
        return () => {
            if (observer) {
                observer.disconnect()
            }
            document.body.classList.remove('has-practice-dock')
            root.style.removeProperty('--practice-dock-space')
            // Don't drop a change made just before leaving.
            if (saveTimer.current) {
                saveTunerSettingsNow()
            }
        }
    }, [])

    // Bound once; the handler itself is replaced on every render so it sees
    // the current tools and state.
    useEffect(() => {
        const onKeyDown = (e) => shortcutRef.current(e)
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    // The drone is on the song's key, so a new song (or key) ends it.
    useEffect(() => {
        player.stopDrone()
        setDroneOn(false)
    }, [songId, songKey])

    // Declared last so it runs after the tools have stopped.
    useEffect(() => () => {
        player.stopDrone()
        closeAudio()
    }, [])

    const toggleDrone = () => {
        if (droneOn) {
            player.stopDrone()
            setDroneOn(false)
            return
        }
        const key = parseChord(songKey)
        if (key) {
            player.startDrone(key.root)
            setDroneOn(true)
            track('drone_started')
        }
    }

    const stopEverything = () => {
        backingTrack.stop()
        backingTrack.stopFollowing()
        transport.stopAll()
        player.stopDrone()
        setDroneOn(false)
    }

    const toggleTool = (id) => {
        const next = openTool === id ? null : id
        setOpenTool(next)
        // The tuner would hear the band and the click (and move the marker
        // if following by ear), and you'd want quiet to tune anyway.
        if (next === 'tuner') {
            backingTrack.stop()
            backingTrack.stopFollowing()
            transport.stopAll()
            player.stopDrone()
            setDroneOn(false)
        }
        if (next) {
            track('dock_tool_opened', { tool: id })
        }
    }

    const requireSubscription = (feature = 'tuner') => {
        // Close the tool (and its microphone) behind the paywall.
        setOpenTool(null)
        dispatch(openModal('modal-paywall', feature))
    }

    const hasChords = backingTrack.chordNames.length > 0

    const tools = [
        {
            id: 'tuner',
            label: gettext('Tuner'),
            icon: <SvgTuningFork />,
            panel: () => (
                <Tuner
                    instruments={instruments}
                    instrumentPreference={instrumentPreference}
                    settings={tunerSettings}
                    onChangeSettings={changeTunerSettings}
                    isSubscriber={isSubscriber}
                    onRequireSubscription={requireSubscription}
                />
            ),
        },
    ]
    if (isAudioSupported()) {
        tools.push({
            id: 'metronome',
            label: gettext('Metronome'),
            icon: <SvgMetronome />,
            on: transport.click,
            primary: () => (transport.click ? transport.stopClick() : transport.startClick()),
            panel: () => (
                <Metronome
                    transport={transport}
                    bandBeats={backingTrack.preset.beats}
                    looping={!!backingTrack.section}
                    isSubscriber={isSubscriber}
                    onRequireSubscription={() => requireSubscription('metronome')}
                />
            ),
        })
    }
    if (canUseBand && backingTrack.available) {
        tools.push({
            id: 'backing-track',
            label: gettext('Backing track'),
            short: gettext('Band'),
            icon: <SvgDrum />,
            pitched: true,
            on: backingTrack.playing,
            primary: () => (backingTrack.playing ? backingTrack.stop() : backingTrack.start()),
            panel: () => <BackingTrack track={backingTrack} transport={transport} />,
        })
    }
    if (hasChords) {
        tools.push({
            id: 'chords',
            label: gettext('Chords'),
            icon: <SvgChordGrid />,
            pitched: true,
            on: backingTrack.following || droneOn,
            // Following (the band follows you anyway while it plays).
            primary: () => {
                if (backingTrack.playing) {
                    return
                }
                if (backingTrack.following) {
                    backingTrack.stopFollowing()
                } else {
                    backingTrack.startFollowing()
                }
            },
            panel: () => (
                <ChordsPanel
                    track={backingTrack}
                    chordsByName={chordsByName}
                    tuning={tuning}
                    player={player}
                    songKey={songKey}
                    droneOn={droneOn}
                    onToggleDrone={toggleDrone}
                />
            ),
        })
        tools.push({
            id: 'changes',
            label: gettext('Chord changes'),
            short: gettext('Changes'),
            icon: <SvgChordChange />,
            pitched: true,
            on: trainerRunning,
            panel: () => (
                <ChangesTrainer
                    songId={songId}
                    chordNames={backingTrack.chordNames}
                    chordsByName={chordsByName}
                    tuning={tuning}
                    player={player}
                    capo={soundingCapo}
                    instrumentId={instrumentPreference}
                    isSubscriber={isSubscriber}
                    isAuthenticated={!!user.isAuthenticated}
                    onRequireSubscription={() => requireSubscription('chord-changes')}
                    onRunningChange={setTrainerRunning}
                    onBeforeStart={() => {
                        backingTrack.stop()
                        backingTrack.stopFollowing()
                        player.stopDrone()
                        setDroneOn(false)
                    }}
                />
            ),
        })
    }
    tools.push({
        id: 'log',
        label: gettext('Practice log'),
        short: gettext('Log'),
        icon: <SvgPracticeLog />,
        on: practiceLog.timerOn,
        primary: () => (practiceLog.timerOn ? practiceLog.stopTimer() : practiceLog.startTimer()),
        panel: () => (
            <PracticeLog
                log={practiceLog}
                songId={songId}
                isAuthenticated={!!user.isAuthenticated}
                onLogin={() => dispatch(openModal('modal-login'))}
            />
        ),
    })
    const active = openTool === 'shortcuts' ?
        { id: 'shortcuts', label: gettext('Keyboard shortcuts'), panel: () => <ShortcutsHelp tools={tools} /> } :
        tools.find(t => t.id === openTool)

    shortcutRef.current = (e) => {
        if (!isShortcut(e)) {
            return
        }
        const key = e.key.length === 1 ? e.key.toLowerCase() : e.key
        // Holding a key down repeats only the tempo steps.
        if (e.repeat && !['+', '=', '-', '_'].includes(key)) {
            return
        }
        const tool = tools.find(t => TOOL_KEYS[t.id] === key)
        // What the key did, for counting which shortcuts get used.
        let shortcut = null
        if (key === 'Escape') {
            shortcut = openTool ? 'close' : null
            setOpenTool(null)
        } else if (tool) {
            shortcut = 'open_tool'
            toggleTool(tool.id)
        } else if (key === HELP_KEY) {
            shortcut = 'help'
            setOpenTool(openTool === 'shortcuts' ? null : 'shortcuts')
        } else if (key === STOP_KEY) {
            shortcut = 'stop'
            stopEverything()
        } else if (key === '+' || key === '=') {
            shortcut = 'tempo'
            transport.nudgeTempo(1)
        } else if (key === '-' || key === '_') {
            shortcut = 'tempo'
            transport.nudgeTempo(-1)
        } else if (key === 'Enter') {
            shortcut = 'tap_tempo'
            transport.tapTempo()
        } else if (key === NEXT_KEY && hasChords) {
            shortcut = 'next_chord'
            const next = upcomingChord(backingTrack)
            if (next) {
                player.strum(chordNotes(parseChord(next), chordsByName[next], tuning))
                trackOnce('chord_strummed', { via: 'key' }, 'key')
            }
        } else if (key === DRONE_KEY && parseChord(songKey)) {
            shortcut = 'drone'
            toggleDrone()
        } else if (key === ' ' && active && active.primary) {
            shortcut = 'space'
            active.primary()
        }
        if (shortcut) {
            e.preventDefault()
            trackOnce('dock_shortcut_used', { action: shortcut }, shortcut)
        }
    }
    // Stop everything, offered while something is going that its open panel
    // (if any) doesn't already have a stop button for.
    const showStop = tools.some(t => t.on && t.id !== 'log' && (!active || t.id !== active.id))
    const beatOn = transport.beat && transport.beat.bar >= 0

    return createPortal(
        <div ref={dockRef} className={classNames('practice-dock', dockPosition.className, { 'practice-dock--open': !!active, 'practice-dock--keys': keyHints })} style={dockPosition.style}>
            {active &&
                <section id="practice-dock-panel" className="practice-dock-panel" aria-label={active.label}>
                    <div className="practice-dock-panel-header">
                        <h2>{active.label}</h2>
                        {active.pitched && capo > 0 &&
                            <label className="practice-check practice-dock-capo" title={gettext('Tick this if you play with the capo on. The chords sound higher then, and the practice tools listen for and play them that way.')}>
                                <input type="checkbox" checked={capoOn} onChange={toggleCapo} />
                                {interpolate(gettext('Capo on fret %s'), [capo])}
                            </label>
                        }
                        <button
                            type="button"
                            className="practice-dock-keys"
                            aria-label={gettext('Keyboard shortcuts')}
                            aria-pressed={openTool === 'shortcuts'}
                            title={interpolate(gettext('Keyboard shortcuts (?). Hold %s to see the keys on the buttons.'), [HINTS_KEY_LABEL])}
                            onClick={() => setOpenTool(openTool === 'shortcuts' ? null : 'shortcuts')}
                        >
                            <span aria-hidden="true">⌨</span>
                            <KeyHint k="?" />
                        </button>
                        <button type="button" className="practice-dock-close" aria-label={gettext('Close')} onClick={() => setOpenTool(null)}>
                            <span aria-hidden="true">×</span>
                            <KeyHint k="Esc" />
                        </button>
                    </div>
                    {active.panel()}
                </section>
            }
            <div ref={dockPosition.barRef} className="practice-dock-bar" role="toolbar" aria-label={gettext('Practice tools')}>
                <DockHandle dock={dockPosition} />
                {tools.map(tool =>
                    <button
                        key={tool.id}
                        type="button"
                        className={classNames('practice-dock-tab', { active: tool.id === openTool, on: tool.on })}
                        aria-expanded={tool.id === openTool}
                        aria-controls={tool.id === openTool ? 'practice-dock-panel' : undefined}
                        aria-label={tool.label}
                        aria-keyshortcuts={TOOL_KEYS[tool.id].toUpperCase()}
                        title={`${tool.label} (${TOOL_KEYS[tool.id].toUpperCase()})`}
                        onClick={(e) => {
                            toggleTool(tool.id)
                            // A clicked tab would otherwise keep the focus, and the
                            // next Space would press it again (closing the panel)
                            // instead of starting the tool. Keyboard users keep it.
                            if (e.detail > 0) {
                                e.currentTarget.blur()
                            }
                        }}
                    >
                        <span className="practice-dock-icon">
                            {tool.icon}
                            {tool.id === 'metronome' && transport.click &&
                                <span key={beatOn ? `${transport.beat.bar}-${transport.beat.beat}` : 'off'} className={classNames('practice-dock-pulse', { accent: beatOn && transport.beat.beat === 0 })} />
                            }
                        </span>
                        <span className="practice-dock-label" aria-hidden="true">{tool.short || tool.label}</span>
                        <KeyHint k={TOOL_KEYS[tool.id].toUpperCase()} />
                        {tool.id === (backingTrack.playing ? 'backing-track' : 'chords') && backingTrack.active && backingTrack.live.chord &&
                            <span className="practice-dock-badge" aria-live="polite">{backingTrack.live.chord}</span>
                        }
                    </button>
                )}
                {showStop &&
                    <button type="button" className="practice-dock-tab practice-dock-stop" aria-label={gettext('Stop')} aria-keyshortcuts="S" title={`${gettext('Stop')} (S)`} onClick={stopEverything}>
                        <span className="practice-dock-stop-icon" aria-hidden="true" />
                        <KeyHint k="S" />
                    </button>
                }
            </div>
        </div>,
        document.body
    )
}

function mapStateToProps(state) {
    const preferences = (state.profile && state.profile.ui_preferences) || {}
    const tuner = preferences[TUNER_SETTINGS_KEY]
    return {
        instruments: (state.instruments && state.instruments.data) || [],
        instrumentPreference: preferences.instrument_preference === undefined ? null : preferences.instrument_preference,
        tunerSettings: tuner && typeof tuner === 'object' && !Array.isArray(tuner) ? tuner : {},
        user: state.user || {},
    }
}

export default connectWithStore(store, PracticeDock, mapStateToProps);
